import { ChangeEvent, useEffect, useState } from "react";

import { usePinProjects } from "../mutations";
import { useFilterContext } from "../../../shared/context/FilterContext";
import { useProjects } from "./useProjects";
import { useStages } from "./useStages";

export const useFetchProjects = (isPinned: boolean, portfolioId: string | undefined) => {

    const pinProjects = usePinProjects();

    const { tempFilters, quickFilters, handleUpdateTempFilters, handleResetFilters, handleApplyFilters } = useFilterContext();

    const [searchDraft, setSearchDraft] = useState<string>('');
    const [searchKey, setSearchKey] = useState<string>('');

    const [page, setPage] = useState<number>(1);
    const [limit, setLimit] = useState<number>(25);

    const [status, setStatus] = useState<{
        id: number | null;
        name: string | null
    }>({
        id: null,
        name: null
    });

    const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);

    const { stagesData, isLoading: isStageLoading, isError: isStageError, refetch: refetchStages, removeCachePinned: removeCachePinnedStages, removeCacheUnpinned: removeCacheUnpinnedStages } = useStages({
        searchKey,
        portfolioId,
        isPinned,
        quickFilters: {
            region: quickFilters.region ?? null,
            programme: quickFilters.programme ?? null,
            site: quickFilters.site ?? null
        },
        page, limit
    });

    const { projectData, isLoading: isProjectLoading, isError: isProjectError, pagination, refetch: refetchProjects, removeCachePinned: removeCachePinnedProjects, removeCacheUnpinned: removeCacheUnpinnedProjects } = useProjects({
        searchKey,
        portfolioId,
        isPinned,
        quickFilters: {
            region: quickFilters.region ?? null,
            programme: quickFilters.programme ?? null,
            site: quickFilters.site ?? null
        },
        phase_name: status.name,
        phase_type_id: status.id,
        page, limit
    });

    const handleOpenFilter = () => {
        setIsOpenFilter(true);
    };

    const handleCloseFilter = () => {
        setIsOpenFilter(false);
        const isDifferent = JSON.stringify(tempFilters) !== JSON.stringify(quickFilters);
        if (isDifferent) {
            handleApplyFilters();
        }
    };

    const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchDraft(e.target.value);
    };

    const handleClear = () => {
        setSearchDraft('');
        setSearchKey('');
    };

    const handleSearch = () => {
        setSearchKey(searchDraft);
    };

    const changePage = (page: number) => {
        setPage(page);
    };

    const changeRowsPerPage = (limit: number) => {
        setLimit(limit);
    };

    const handlePinProject = async (id: string, status: boolean) => {
        await pinProjects.mutateAsync({ id: id, status: status });
        removeCachePinnedProjects();
        removeCacheUnpinnedProjects();
        removeCachePinnedStages();
        removeCacheUnpinnedStages();
    };

    useEffect(() => {
        const allPhaseStage = stagesData.find(
            (stage: { name: string; id: any }) => stage.name === 'All Phase'
        );
        if (allPhaseStage) {
            setStatus({
                id: allPhaseStage.id,
                name: allPhaseStage.name
            });
        }
    }, [stagesData, isStageLoading]);

    return {
        pagination,
        isLoading: isProjectLoading || isStageLoading,
        isError: isProjectError,
        searchDraft,
        projects: projectData,
        projectStages: stagesData || [],
        status, setStatus,
        handleChangeSearch, handleClear, handleSearch, changePage, changeRowsPerPage,
        handleUpdateTempFilters,
        handleResetFilters,
        handleApplyFilters,
        tempFilters,
        isOpenFilter, handleOpenFilter, handleCloseFilter,
        handlePinProject
    };

};